import React, { useState, useEffect } from 'react';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { PasswordInput } from '@ui/components/PasswordInput';
import { CTAGroup } from '../ui/OnboardingCTA';
import { TextInput } from '../ui/TextInput';
import { PhoneInput } from '../ui/PhoneInput';
import { FormCheckbox } from '../ui/FormCheckbox';
import { INPUT_BASE_CLASSES, cx, extractDigits } from '../ui/formStyles';

type OnboardingCreateAccountStepProps = {
  onBack: () => void;
  onContinue: (data: CreateAccountData) => void | Promise<void>;
  /**
   * Optional initial values used when a seeker navigates back to this step
   * from email verification ("Update email address"). Password is never
   * prefilled — we don't store it after submission.
   */
  initialData?: {
    firstName?: string;
    email?: string;
    phone?: string;
  };
};

export type CreateAccountData = {
  firstName: string;
  email: string;
  confirmEmail: string;
  phone: string;
  password: string;
  agreedToTerms: boolean;
  marketingOptIn: boolean;
};

/**
 * Split a stored full phone (e.g. "+15551234567") into its country code
 * and local digits. Falls back to "+1" if no recognisable prefix.
 */
function splitStoredPhone(stored: string | undefined): { countryCode: string; phoneNumber: string } {
  if (!stored) return { countryCode: '+1', phoneNumber: '' };
  const match = stored.match(/^(\+\d{1,3})(\d+)$/);
  if (match) {
    return { countryCode: match[1], phoneNumber: match[2] };
  }
  // No recognisable country code prefix — assume US and use all digits.
  const digits = stored.replace(/\D/g, '');
  return { countryCode: '+1', phoneNumber: digits };
}

type ErrorMap = Record<string, string | undefined>;

function BenefitIcon() {
  return (
    <svg
      width="18"
      height="21"
      viewBox="0 0 18 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.00005 3C10.2638 3 11.2882 4.02443 11.2882 5.28814V18.7119C11.2882 19.9756 10.2638 21 9.00005 21C7.73635 21 6.71191 19.9756 6.71191 18.7119V5.28814C6.71191 4.02443 7.73635 3 9.00005 3Z"
        fill="#CA9D35"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.72858e-08 11.9999C4.23553e-08 10.7362 1.02443 9.71181 2.28814 9.71181L15.7119 9.71181C16.9756 9.71181 18 10.7362 18 11.9999C18 13.2637 16.9756 14.2881 15.7119 14.2881L2.28814 14.2881C1.02443 14.2881 1.22163e-08 13.2637 2.72858e-08 11.9999Z"
        fill="#CA9D35"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.63553 5.63595C3.52911 4.74238 4.97788 4.74238 5.87145 5.63595L15.3635 15.128C16.257 16.0215 16.257 17.4703 15.3635 18.3639C14.4699 19.2574 13.0211 19.2574 12.1276 18.3639L2.63554 8.87186C1.74196 7.97829 1.74196 6.52952 2.63553 5.63595Z"
        fill="#CA9D35"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.63595 18.364C1.74238 17.4704 1.74238 16.0216 2.63595 15.1281L12.128 5.63605C13.0215 4.74247 14.4703 4.74247 15.3639 5.63605C16.2574 6.52962 16.2575 7.97839 15.3639 8.87196L5.87186 18.364C4.97829 19.2575 3.52952 19.2575 2.63595 18.364Z"
        fill="#CA9D35"
      />
    </svg>
  );
}

const benefits = [
  'Save your answers',
  'Protect your privacy',
  'Explore aligned guides'
];

export function OnboardingCreateAccountStep({
  onBack,
  onContinue,
  initialData
}: OnboardingCreateAccountStepProps) {
  // Seed state from initialData on first mount (e.g., returning from the
  // email verification step via "Update email address"). These only run once;
  // subsequent edits stay in local state and aren't overwritten by parent
  // re-renders.
  const initialPhoneSplit = splitStoredPhone(initialData?.phone);
  const [firstName, setFirstName] = useState(initialData?.firstName ?? '');
  const [email, setEmail] = useState(initialData?.email ?? '');
  const [confirmEmail, setConfirmEmail] = useState(initialData?.email ?? '');
  const [countryCode, setCountryCode] = useState(initialPhoneSplit.countryCode);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneSplit.phoneNumber);
  const [password, setPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [errors, setErrors] = useState<ErrorMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const trimmed = email.trim().toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      localStorage.setItem('gc_lead_email', trimmed);
    }
  }, [email]);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: ErrorMap = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required.';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email.';
    } else if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
      newErrors.confirmEmail = 'Emails do not match.';
    }

    if (!phoneNumber.trim()) {
      newErrors.phone = 'Phone number is required.';
    } else {
      const digits = extractDigits(phoneNumber);
      if (countryCode === '+1' && digits.length !== 10) {
        newErrors.phone = 'Please enter a valid 10-digit phone number.';
      } else if (digits.length < 7 || digits.length > 15) {
        newErrors.phone = 'Please enter a valid phone number.';
      }
    }

    if (!password) {
      newErrors.password = 'Password is required.';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters.';
    }

    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the terms to continue.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    if (submitting) return;
    if (!validate()) return;

    const fullPhone = `${countryCode}${extractDigits(phoneNumber)}`;
    setSubmitError(undefined);
    setSubmitting(true);
    try {
      await onContinue({
        firstName: firstName.trim(),
        email: email.trim().toLowerCase(),
        confirmEmail: confirmEmail.trim().toLowerCase(),
        phone: fullPhone,
        password,
        agreedToTerms,
        marketingOptIn,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#fefefe]">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-onboarding-bg/25 bg-onboarding-header px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:items-center lg:px-[72px] lg:py-[40px] lg:gap-[40px]">
          {/* Main content area */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center gap-[24px] overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-[40px] pt-6 lg:pt-0 w-full lg:w-[509px]">
            {/* Title */}
            <h2 className="font-recoleta text-[32px] text-[#214d3c] tracking-[-0.448px] leading-[44px] w-full">
              Create your account
            </h2>

            {/* Benefits card */}
            <div className="border-2 border-[#214d3c] rounded-[16px] p-[24px] w-full">
              <div className="flex flex-col gap-[16px]">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-[12px]">
                    <BenefitIcon />
                    <span className="font-recoleta text-[24px] text-[#214d3c] leading-[28px]">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form fields */}
            <div className="flex flex-col gap-[16px] w-full">
              {/* First name */}
              <TextInput
                id="firstName"
                value={firstName}
                onChange={(value) => {
                  setFirstName(value);
                  clearError('firstName');
                }}
                placeholder="First name (or nickname)"
                error={errors.firstName}
              />

              {/* Email */}
              <TextInput
                id="email"
                type="email"
                value={email}
                onChange={(value) => {
                  setEmail(value);
                  clearError('email');
                }}
                placeholder="Email"
                error={errors.email}
              />

              {/* Confirm email */}
              <TextInput
                id="confirmEmail"
                type="email"
                value={confirmEmail}
                onChange={(value) => {
                  setConfirmEmail(value);
                  clearError('confirmEmail');
                }}
                placeholder="Confirm email"
                error={errors.confirmEmail}
              />

              {/* Phone number */}
              <PhoneInput
                id="phone"
                countryCode={countryCode}
                phoneNumber={phoneNumber}
                onCountryCodeChange={setCountryCode}
                onPhoneNumberChange={(value) => {
                  setPhoneNumber(value);
                  clearError('phone');
                }}
                error={errors.phone}
              />

              {/* Password */}
              <div className="flex flex-col gap-[8px]">
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearError('password');
                  }}
                  placeholder="Create password"
                  className={cx(
                    INPUT_BASE_CLASSES,
                    "font-['Helvetica Neue','Arial',sans-serif]",
                    'placeholder:text-[#707C8B]',
                    errors.password && 'border-red-600'
                  )}
                />
                <p className="text-[14px] text-[#707C8B] leading-[1.6]">
                  Please use at least 8 characters
                </p>
                {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
              </div>

              {/* Terms checkbox */}
              <FormCheckbox
                id="terms"
                checked={agreedToTerms}
                onChange={(checked) => {
                  setAgreedToTerms(checked);
                  clearError('terms');
                }}
                error={errors.terms}
              >
                I agree to{' '}
                <a href="/terms" target="_blank" className="underline text-[#214d3c]">
                  Terms & Conditions
                </a>
                ,{' '}
                <a href="/privacy" target="_blank" className="underline text-[#214d3c]">
                  Privacy Policy
                </a>
                , and{' '}
                <a href="/privacy-practices" target="_blank" className="underline text-[#214d3c]">
                  Notice of Privacy Practices
                </a>
              </FormCheckbox>

              {/* Marketing consent checkbox */}
              <FormCheckbox
                id="marketing"
                checked={marketingOptIn}
                onChange={(checked) => setMarketingOptIn(checked)}
              >
                I consent to receive occasional marketing emails about new guides, offerings, and community resources. You can opt out anytime.
              </FormCheckbox>
            </div>

            {/* Helper text */}
            <p className="text-[14px] leading-[1.6] text-black w-full">
              Next, we'll ask a few quick questions about your background. This will help us match you with compassionate and compatible guides.
            </p>

            {submitError && (
              <p role="alert" className="text-sm text-red-600 w-full">{submitError}</p>
            )}

            {/* CTA */}
            <div className="py-4 w-full">
              <CTAGroup
                onBack={onBack}
                onContinue={handleContinue}
                isLoading={submitting}
                continueDisabled={submitting}
                className="w-full"
              />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
